<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { getI18n } from '@/plugins/i18n'
import 'gridstack/dist/gridstack.min.css'

import { useDetectDevice } from '@/composables/device'
import { useWebSiteStore, elementLayoutTypeEnum } from '@/stores/web-site.store'
import { useDashboardStore } from '@/stores/dashboard.store'
import { useDashboardGrid } from '@/composables/dashboard-grid'
import { getPeriodLabel } from '@/composables/dashboard-kpi'
import { TILE_CHARTS, TILE_LIST_KEYS } from '@/components/dashboard/tile-charts'
import { planningApi } from '@/apis/planning.api'

import AddTileDialog from '@/components/dashboard/AddTileDialog.vue'
import DashboardTile from '@/components/dashboard/DashboardTile.vue'
import KpiMetricTile from '@/components/dashboard/charts/KpiMetricTile.vue'
import KpiChartTile from '@/components/dashboard/charts/KpiChartTile.vue'
import TemplateTile from '@/components/dashboard/TemplateTile.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ElementLayoutSelector from '@/components/shared/ElementLayoutSelector.vue'

const PAGE_OPTIONS = [
  { label: 'Default',   value: 'all',       icon: 'mdi-view-grid-outline' },
  { label: 'Projects',  value: 'projects',   icon: 'mdi-briefcase-outline' },
  { label: 'ROI',       value: 'roi',        icon: 'mdi-chart-line' },
  { label: 'Warehouse', value: 'warehouse',  icon: 'mdi-warehouse' },
]

const MOBILE_TILE_HEIGHT = {
  'active-projects-count': '200px',
  'avg-project-duration':  '200px',
  'pending-approvals':     '520px',
  'equipment-attention':   '200px',
  'gross-margin':          '420px',
}

const customTiles = ref([])
const showAddTile = ref(false)
const warehouses = ref([])

// ── Named layouts ───────────────────────────────────────────────────────────
const pageBuiltins = computed(() => PAGE_OPTIONS.map(o => ({
  value: o.value,
  icon: o.icon,
  label: o.value === 'all' ? t('dashboard.defaultLayout') : o.label,
})))

const currentLayoutData = () => ({
  ...getServerLayout(),
  [currentPage.value]: {
    hidden: [...hiddenTileIds.value],
    positions: { ...tilePositions.value },
    kpi: { ...kpiConfig.value },
    custom_titles: { ...tileCustomTitles.value },
    custom_tiles: customTiles.value,
  },
})

const applyLayoutData = async (layoutData) => {
  await webSiteStore.saveDashboardLayoutAsync(layoutData)
  loadViewLayout(currentPage.value)
  await reinitGrid()
}

const onLoadLayout = (layout) => applyLayoutData(layout.data)

const onDefaultResolved = ({ builtin, layout }) => {
  if (Object.keys(getServerLayout()).length > 0) {
    return
  }
  if (layout) {
    applyLayoutData(layout.data)
  } else if (builtin && builtin !== 'all') {
    onLoadDefaultLayout(builtin)
  }
}

const getMobileHeight = (id) => {
  if (id.startsWith('custom-')) {
    const ct = customTiles.value.find(t => t.id === id)
    return ct?.type === 'kpi' ? '200px' : '320px'
  }
  return MOBILE_TILE_HEIGHT[id] ?? '280px'
}

const { t } = getI18n()
const { isMobile } = useDetectDevice()
const webSiteStore = useWebSiteStore()
const dashboardStore = useDashboardStore()
const gridEl = ref(null)
const currentPage = ref('all')

const hiddenTileIds = ref(new Set())
const tilePositions = ref({})
const kpiConfig = ref({})
const tileCustomTitles = ref({})
const tileRenderKeys = ref({})

const getServerLayout = () => {
  const layout = webSiteStore.user?.dashboard_layout
  if (!layout || Array.isArray(layout)) return {}
  return layout
}

const loadViewLayout = (view) => {
  const viewData = getServerLayout()[view] ?? {}
  hiddenTileIds.value = new Set(viewData.hidden ?? [])
  tilePositions.value = viewData.positions ?? {}
  kpiConfig.value = viewData.kpi ?? {}
  tileCustomTitles.value = viewData.custom_titles ?? {}
  customTiles.value = viewData.custom_tiles ?? []
}

const saveViewLayout = async (view) => {
  const updated = {
    ...getServerLayout(),
    [view]: {
      hidden: [...hiddenTileIds.value],
      positions: { ...tilePositions.value },
      kpi: { ...kpiConfig.value },
      custom_titles: { ...tileCustomTitles.value },
      custom_tiles: customTiles.value,
    },
  }
  await webSiteStore.saveDashboardLayoutAsync(updated)
}

const resolveCustomTile = (ct) => {
  const defaultPos = { x: 0, y: 999, w: ct.defaultW ?? 4, h: ct.defaultH ?? 7 }
  const pos = tilePositions.value[ct.id] ?? defaultPos
  return { ...ct, title: ct.customTitle, ...pos }
}

const getTileMetric = (tileId) => {
  const v = kpiConfig.value[tileId]
  if (typeof v === 'string') return v
  if (v && typeof v === 'object' && v.metric) return v.metric
  const ct = customTiles.value.find(t => t.id === tileId)
  return ct?.metric ?? null
}

const getTileFilters = (tileId) => {
  const v = kpiConfig.value[tileId]
  if (v && typeof v === 'object') {
    return {
      warehouseIds: v.warehouseIds ?? [],
      periodType: v.periodType ?? 'all',
      periodFrom: v.periodFrom ?? null,
      periodTo: v.periodTo ?? null,
    }
  }
  const ct = customTiles.value.find(t => t.id === tileId)
  return {
    warehouseIds: ct?.warehouseIds ?? [],
    periodType: ct?.periodType ?? 'all',
    periodFrom: ct?.periodFrom ?? null,
    periodTo: ct?.periodTo ?? null,
  }
}

const getTileChartConfig = (tileId) => {
  const v = kpiConfig.value[tileId]
  if (v && typeof v === 'object') return v
  const ct = customTiles.value.find(t => t.id === tileId)
  if (ct) return {
    metric: ct.metric ?? 'new_projects_this_month',
    chartType: ct.chartType ?? 0,
    period: 0,
    warehouseIds: ct.warehouseIds ?? [],
    periodType: ct.periodType ?? 'all',
    periodFrom: ct.periodFrom ?? null,
    periodTo: ct.periodTo ?? null,
  }
  return {}
}

const getKpiTitleKey = (tileId) => {
  const v = kpiConfig.value[tileId]
  let metricKey
  if (dashboardStore.kpiChartTileIds.has(tileId)) {
    metricKey = (v && typeof v === 'object' ? v.metric : null) ?? dashboardStore.kpiChartDefaultByTile[tileId]
  } else {
    metricKey = (typeof v === 'string' ? v : (v?.metric ?? null)) ?? dashboardStore.kpiDefaultByTile[tileId]
  }
  return dashboardStore.kpiMetric(metricKey)?.titleKey ?? null
}

const getTileHeaderTitleKey = (tileId) => {
  if (tileCustomTitles.value[tileId]) return tileCustomTitles.value[tileId]
  if (tileId.startsWith('custom-')) return null
  if (dashboardStore.kpiTileIds.has(tileId) || dashboardStore.kpiChartTileIds.has(tileId)) return getKpiTitleKey(tileId)
  return null
}

const getTileName = (tileId) => {
  if (tileCustomTitles.value[tileId]) return tileCustomTitles.value[tileId]
  const titleKey = getKpiTitleKey(tileId)
  if (titleKey) return t(titleKey)
  const tile = visibleTiles.value.find(tile => tile.id === tileId)
  return tile ? t(tile.title) : ''
}

const onChartConfigChange = async (tileId, config) => {
  kpiConfig.value = { ...kpiConfig.value, [tileId]: config }
  await saveViewLayout(currentPage.value)
}

const getTilePeriodSuffix = (tileId) => getPeriodLabel(getTileFilters(tileId), t)

const getTileWarehouseNames = (tileId) => {
  const ids = getTileFilters(tileId)?.warehouseIds ?? []
  return ids.map(id => warehouses.value.find(w => w.id === id)?.name).filter(Boolean)
}

let saveTimer = null
const scheduleSave = () => {
  clearTimeout(saveTimer)
  saveTimer = setTimeout(() => saveViewLayout(currentPage.value), 800)
}

const onGridLayoutChange = (changedPositions) => {
  tilePositions.value = { ...tilePositions.value, ...changedPositions }
  scheduleSave()
}

const getTilePos = (tile) => tilePositions.value[tile.id] ?? { x: tile.x, y: tile.y, w: tile.w, h: tile.h }

const isScrollableTile = (tile) => TILE_LIST_KEYS.has(tile.type) || TILE_LIST_KEYS.has(tile.id)

const visibleTiles = computed(() => {
  const fixed = dashboardStore.resolvePageTiles(currentPage.value).filter(t => !hiddenTileIds.value.has(t.id))
  const custom = customTiles.value
    .filter(ct => !hiddenTileIds.value.has(ct.id))
    .map(resolveCustomTile)
  return [...fixed, ...custom]
})


const { init: initGrid, destroy } = useDashboardGrid()

const onTileResizeStop = (tileId) => {
  tileRenderKeys.value[tileId] = (tileRenderKeys.value[tileId] ?? 0) + 1
}

const reinitGrid = async () => {
  destroy()
  await nextTick()
  if (!isMobile.value) initGrid(gridEl.value, onGridLayoutChange, onTileResizeStop)
}

const onLoadDefaultLayout = async (page) => {
  clearTimeout(saveTimer)
  destroy()
  currentPage.value = page
  tilePositions.value = {}
  hiddenTileIds.value = new Set()
  kpiConfig.value = {}
  tileCustomTitles.value = {}
  customTiles.value = []
  const { [page]: _removed, ...rest } = getServerLayout()
  void _removed
  await webSiteStore.saveDashboardLayoutAsync(rest)
  if (!isMobile.value) {
    await nextTick()
    initGrid(gridEl.value, onGridLayoutChange, onTileResizeStop)
  }
}

const editingTile = ref(null)
const isTileEditable = (tile) =>
  (tile.id.startsWith('custom-') && (tile.type === 'kpi' || tile.type === 'chart' || !!TILE_CHARTS[tile.type]))
  || dashboardStore.kpiTileIds.has(tile.id)
  || dashboardStore.kpiChartTileIds.has(tile.id)

const buildEditTile = (tileId) => {
  const tile = visibleTiles.value.find(t => t.id === tileId)
  const filters = getTileFilters(tileId)
  const base = {
    id: tileId,
    title: getTileName(tileId),
    warehouseIds: filters.warehouseIds,
    periodType: filters.periodType,
    periodFrom: filters.periodFrom,
    periodTo: filters.periodTo,
  }
  if (tileId.startsWith('custom-') && tile?.type && tile.type !== 'kpi' && tile.type !== 'chart') {
    return { ...base, kind: 'template', source: { kind: 'chart', id: tile.type } }
  }
  const lockDisplay = !tileId.startsWith('custom-')
  if ((tileId.startsWith('custom-') && tile?.type === 'chart') || dashboardStore.kpiChartTileIds.has(tileId)) {
    const cfg = getTileChartConfig(tileId)
    return {
      ...base,
      kind: 'custom',
      displayType: 'chart',
      chartType: cfg.chartType ?? 0,
      source: { kind: 'metric', id: cfg.metric ?? dashboardStore.kpiChartDefaultByTile[tileId] },
      lockDisplay,
    }
  }
  return {
    ...base,
    kind: 'custom',
    displayType: 'kpi',
    source: { kind: 'metric', id: getTileMetric(tileId) ?? dashboardStore.kpiDefaultByTile[tileId] },
    lockDisplay,
  }
}

const onEditTile = (tileId) => {
  editingTile.value = buildEditTile(tileId)
  showAddTile.value = true
}

const onUpdateTile = async (p) => {
  const id = p.id
  const chartCfg = {
    metric: p.metric,
    chartType: p.chartType,
    period: kpiConfig.value[id]?.period ?? 0,
    warehouseIds: p.warehouseIds,
    periodType: p.periodType,
    periodFrom: p.periodFrom,
    periodTo: p.periodTo,
  }
  if (id.startsWith('custom-')) {
    customTiles.value = customTiles.value.map(ct => ct.id === id ? {
      ...ct,
      type: p.type,
      metric: p.metric,
      chartType: p.chartType,
      customTitle: p.customTitle,
      warehouseIds: p.warehouseIds,
      periodType: p.periodType,
      periodFrom: p.periodFrom,
      periodTo: p.periodTo,
    } : ct)
    // kpiConfig shadows customTiles in the getters; keep it in sync for chart tiles, drop it otherwise
    if (p.type === 'chart') {
      kpiConfig.value = { ...kpiConfig.value, [id]: chartCfg }
    } else {
      const { [id]: _drop, ...restKpi } = kpiConfig.value
      void _drop
      kpiConfig.value = restKpi
    }
  } else {
    kpiConfig.value = {
      ...kpiConfig.value,
      [id]: p.type === 'chart'
        ? chartCfg
        : { metric: p.metric, warehouseIds: p.warehouseIds, periodType: p.periodType, periodFrom: p.periodFrom, periodTo: p.periodTo },
    }
  }
  tileCustomTitles.value = { ...tileCustomTitles.value, [id]: p.customTitle }
  editingTile.value = null
  tileRenderKeys.value[id] = (tileRenderKeys.value[id] ?? 0) + 1
  await saveViewLayout(currentPage.value)
}

const onOpenAddTile = () => {
  editingTile.value = null
  showAddTile.value = true
}

const deleteTileId = ref(null)
const showDeleteTileDialog = computed({
  get: () => deleteTileId.value !== null,
  set: (val) => { if (!val) deleteTileId.value = null },
})

const onDeleteTile = (tileId) => { deleteTileId.value = tileId }

const onDeleteTileConfirmed = async () => {
  const tileId = deleteTileId.value
  if (tileId.startsWith('custom-')) {
    customTiles.value = customTiles.value.filter(ct => ct.id !== tileId)
  } else {
    hiddenTileIds.value = new Set([...hiddenTileIds.value, tileId])
  }
  const { [tileId]: _removed, ...rest } = tilePositions.value
  void _removed
  tilePositions.value = rest
  deleteTileId.value = null
  await saveViewLayout(currentPage.value)
  await reinitGrid()
}


const onAddTile = async (tileConfig) => {
  const maxY = visibleTiles.value.reduce((max, t) => {
    const pos = getTilePos(t)
    return Math.max(max, pos.y + pos.h)
  }, 0)
  customTiles.value = [...customTiles.value, tileConfig]
  tilePositions.value = { ...tilePositions.value, [tileConfig.id]: { x: 0, y: maxY, w: tileConfig.defaultW, h: tileConfig.defaultH } }
  await saveViewLayout(currentPage.value)
  await reinitGrid()
}

onMounted(async () => {
  webSiteStore.fabActions = []
  webSiteStore.pageTabOptions = []
  webSiteStore.currentPageTab = null
  webSiteStore.onPageTabChange = null
  await dashboardStore.fetchDashboardCatalogAsync()
  loadViewLayout(currentPage.value)
  planningApi.getWarehousesAsync({ all: true }).then(r => { warehouses.value = r.data })
  if (!isMobile.value) {
    await nextTick()
    initGrid(gridEl.value, onGridLayoutChange, onTileResizeStop)
  }
})

onUnmounted(() => {
  clearTimeout(saveTimer)
  webSiteStore.pageTabOptions = []
  webSiteStore.currentPageTab = null
  webSiteStore.onPageTabChange = null
  destroy()
})
</script>

<template>
  <q-page class="dashboard-page">

    <!-- Desktop layout -->
    <template v-if="!isMobile">
      <div class="grid-wrapper">
        <div class="row justify-end q-pb-xs q-gutter-xs items-center">
          <element-layout-selector
            :element-type="elementLayoutTypeEnum.DASHBOARD"
            :builtins="pageBuiltins"
            :initial-builtin="currentPage"
            :capture-data="currentLayoutData"
            :menu-title="t('common.layout')"
            icon="mdi-view-dashboard-edit-outline"
            :supports-default="true"
            @select-builtin="onLoadDefaultLayout"
            @load="onLoadLayout"
            @default-resolved="onDefaultResolved"
          />

          <q-btn
            round
            flat
            dense
            icon="mdi-plus"
            color="grey-6"
            @click="onOpenAddTile"
          >
            <q-tooltip :delay="400">{{ t('dashboard.addTile') }}</q-tooltip>
          </q-btn>
        </div>

        <div ref="gridEl" class="grid-stack">
          <div
            v-for="tile in visibleTiles"
            :key="tile.id"
            class="grid-stack-item"
            :gs-id="tile.id"
            :gs-x="getTilePos(tile).x"
            :gs-y="getTilePos(tile).y"
            :gs-w="getTilePos(tile).w"
            :gs-h="getTilePos(tile).h"
            :gs-min-w="Math.ceil(tile.w / 2)"
            :gs-min-h="Math.ceil(tile.h / 2)"
          >
            <div class="grid-stack-item-content">
              <dashboard-tile
                :tile="tile"
                :editable="isTileEditable(tile)"
                :title-key="getTileHeaderTitleKey(tile.id)"
                :title-suffix="getTilePeriodSuffix(tile.id)"
                :warehouse-names="getTileWarehouseNames(tile.id)"
                :scrollable="isScrollableTile(tile)"
                @delete="onDeleteTile(tile.id)"
                @edit="onEditTile(tile.id)"
              >
                <div :key="tileRenderKeys[tile.id]" style="height:100%">
                  <kpi-metric-tile
                    v-if="tile.id.startsWith('custom-') && tile.type === 'kpi'"
                    :tile-id="tile.id"
                    :metric-key="getTileMetric(tile.id)"
                    :tile-filters="getTileFilters(tile.id)"
                    :tile-name="getTileName(tile.id)"
                    :warehouses="warehouses"
                    @edit="onEditTile(tile.id)"
                  />
                  <kpi-chart-tile
                    v-else-if="tile.id.startsWith('custom-') && tile.type === 'chart'"
                    :tile-id="tile.id"
                    :chart-config="getTileChartConfig(tile.id)"
                    :tile-name="getTileName(tile.id)"
                    @config-change="onChartConfigChange(tile.id, $event)"
                    @edit="onEditTile(tile.id)"
                  />
                  <template-tile
                    v-else-if="tile.id.startsWith('custom-') && TILE_CHARTS[tile.type]"
                    :tile-id="tile.id"
                    :tile-type="tile.type"
                    :filters="getTileFilters(tile.id)"
                    :tile-name="getTileName(tile.id)"
                    @edit="onEditTile(tile.id)"
                  />
                  <kpi-metric-tile
                    v-else-if="dashboardStore.kpiTileIds.has(tile.id)"
                    :tile-id="tile.id"
                    :metric-key="getTileMetric(tile.id)"
                    :tile-filters="getTileFilters(tile.id)"
                    :tile-name="getTileName(tile.id)"
                    :warehouses="warehouses"
                    @edit="onEditTile(tile.id)"
                  />
                  <kpi-chart-tile
                    v-else-if="dashboardStore.kpiChartTileIds.has(tile.id)"
                    :tile-id="tile.id"
                    :chart-config="getTileChartConfig(tile.id)"
                    :tile-name="getTileName(tile.id)"
                    @config-change="onChartConfigChange(tile.id, $event)"
                    @edit="onEditTile(tile.id)"
                  />
                  <component :is="TILE_CHARTS[tile.id]" v-else-if="TILE_CHARTS[tile.id]" />
                </div>
              </dashboard-tile>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile layout: absolute fill → flex column → tabs never overlap content -->
    <div v-else class="mobile-layout">
      <div class="mobile-scroll">
        <!-- wrapper div gives the pixel height; DashboardTile's `fit` (height:100%!important) resolves against it -->
        <div
          v-for="tile in visibleTiles"
          :key="tile.id"
          class="mobile-tile-wrapper"
          :style="{ height: getMobileHeight(tile.id) }"
        >
          <dashboard-tile
            :tile="tile"
            :mobile="true"
            :title-suffix="getTilePeriodSuffix(tile.id)"
            :warehouse-names="getTileWarehouseNames(tile.id)"
            :scrollable="isScrollableTile(tile)"
          >
            <kpi-metric-tile
              v-if="(tile.id.startsWith('custom-') && tile.type === 'kpi') || dashboardStore.kpiTileIds.has(tile.id)"
              :tile-id="tile.id"
              :metric-key="getTileMetric(tile.id)"
              :tile-filters="getTileFilters(tile.id)"
              :tile-name="getTileName(tile.id)"
              :warehouses="warehouses"
              @edit="onEditTile(tile.id)"
            />
            <kpi-chart-tile
              v-else-if="(tile.id.startsWith('custom-') && tile.type === 'chart') || dashboardStore.kpiChartTileIds.has(tile.id)"
              :tile-id="tile.id"
              :chart-config="getTileChartConfig(tile.id)"
              :tile-name="getTileName(tile.id)"
              @config-change="onChartConfigChange(tile.id, $event)"
              @edit="onEditTile(tile.id)"
            />
            <template-tile
              v-else-if="tile.id.startsWith('custom-') && TILE_CHARTS[tile.type]"
              :tile-id="tile.id"
              :tile-type="tile.type"
              :filters="getTileFilters(tile.id)"
              :tile-name="getTileName(tile.id)"
              @edit="onEditTile(tile.id)"
            />
            <component :is="TILE_CHARTS[tile.id]" v-else-if="TILE_CHARTS[tile.id]" />
          </dashboard-tile>
        </div>
      </div>

    </div>

    <add-tile-dialog
      v-model="showAddTile"
      :edit-tile="editingTile"
      @add="onAddTile"
      @update="onUpdateTile"
    />

    <confirm-dialog
      v-model="showDeleteTileDialog"
      :message="t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteTileConfirmed"
    />

  </q-page>
</template>

<style scoped>
.dashboard-page {
  padding: 0;
}

/* Desktop */
.grid-wrapper {
  margin: 0 auto;
  width: 100%;
}

/* GridStack's wrapper clips overflow, hiding the q-card shadow; the card clips its own content. */
.grid-stack-item > .grid-stack-item-content {
  overflow: visible;
}

/* Mobile */
.mobile-layout {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.mobile-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mobile-tile-wrapper {
  flex-shrink: 0;
  width: 100%;
}

</style>

<style>
body.grid-stack-dragging,
body.grid-stack-dragging * {
  cursor: move !important;
}
</style>
